import { useContext, useEffect, useState } from 'react'
import { SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { zodResolver } from '@hookform/resolvers/zod'

import {
  useUpdateArticleMutation,
  useUploadArticleImageMutation
} from '@/common/api/modules/articles'
import { useFormErrorMessages, useMessageStates } from '@/common/app/message'
import {
  ARTICLE_SLUG_EXT_STEM_EDITABLE,
  ARTICLE_SLUG_MAX_LENGTH,
  ARTICLE_TITLE_MAX_LENGTH,
  ArticleSlugKind,
  PERSONA_PLACEHOLDER,
  TARGET_CHAR_COUNT_CHOICES,
  TONE_RADIO_CHOICES,
  articleProductListSummary,
  articleStatusBadgeType,
  articleStatusChoices,
  articleStatusLabel
} from '@/common/app/modules/media/article'
import { articleApprovalStatusLabel } from '@/common/app/modules/media/articleApproval'
import { articleDetailPath } from '@/common/app/path'
import { useShowSavedToast } from '@/common/app/toast'
import { dateTimeStr } from '@/common/form/date'
import { registerNumberOption } from '@/common/form/register'
import { StringLength } from '@/common/form/schema'
import {
  ARTICLE_PERSONA_MAX_LENGTH,
  ArticlePartRecord,
  ArticleProposalDetail,
  ArticleStatus
} from '@/common/model/media/article'
import { preventKeyDown } from '@/common/util/keyboard'

import Badge from '@/components/view/atoms/badge/Badge'
import AiButton from '@/components/view/atoms/button/AiButton'
import BackButton from '@/components/view/atoms/button/BackButton'
import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import FormLabel from '@/components/view/atoms/form/FormLabel'
import Heading from '@/components/view/atoms/heading/Heading'
import { SelectorSize } from '@/components/view/atoms/selector/Selector'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import { TextBoxSize } from '@/components/view/atoms/textfield/TextField'
import UploadDropZone, {
  UploadDropZoneImageSize
} from '@/components/view/atoms/upload/UploadDropZone'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import InsideArea, { BgColor } from '@/components/view/layouts/area/InsideArea'
import FormRow from '@/components/view/layouts/form/FormRow'
import Content, { ContentType } from '@/components/view/layouts/page/Content'
import ContentSideFrame from '@/components/view/layouts/page/ContentSideFrame'
import PageBottom from '@/components/view/layouts/page/PageBottom'
import Side from '@/components/view/layouts/page/Side'
import SerpPreview from '@/components/view/molecules/article/SerpPreview'
import { MessageType } from '@/components/view/molecules/bar/MessageBar'
import TopBar, { TopBarStatus } from '@/components/view/molecules/bar/TopBar'
import PageTitle from '@/components/view/molecules/title/PageTitle'
import TooltipWrapper from '@/components/view/utils/tooltip/TooltipWrapper'

import FormDisclosureSelector from '@/components/container/form/widgets/FormDisclosureSelector'
import FormError from '@/components/container/form/widgets/FormError'
import FormMessageList from '@/components/container/form/widgets/FormMessageList'
import FormRadioButton from '@/components/container/form/widgets/FormRadioButton'
import FormSelector from '@/components/container/form/widgets/FormSelector'
import FormTextArea from '@/components/container/form/widgets/FormTextArea'
import FormTextField from '@/components/container/form/widgets/FormTextField'
import { useAppHeaderHiding } from '@/components/container/layout/AppHeaderProvider'
import AppTitleBadge, { AppTitleBadgeType } from '@/components/container/layout/AppTitleBadge'
import { ArticleDiagnosisModalContext } from '@/components/container/modal/article/ArticleDiagnosisModalProvider'
import { ArticleRewriteModalContext } from '@/components/container/modal/article/ArticleRewriteModalProvider'
import { AppliedArticleRewriteFields } from '@/components/container/modal/article/articleRewriteApply'
import ArticleUpdateHistory from '@/components/container/modules/media/article/ArticleUpdateHistory'
import { ToastMessageContext } from '@/components/container/utils/message/ToastMessageProvider'

import {
  ArticleEditSchemaType,
  articleEditNameLabelMapping,
  articleEditSchema,
  convertDetailToSchema,
  convertSchemaToUpdateVars
} from './articleEditSchema'

interface Props {
  siteId: number
  articleProposalId: number
  detail: ArticleProposalDetail
  enableArticleUpload: boolean
  onSaved(): void
}

const findPart = (parts: ArticlePartRecord[], partType: string): ArticlePartRecord | undefined =>
  parts.find((p) => p.part_type === partType)

// 記事編集画面本体（ヘッダー・特集記事概要・コンテンツブロック・利用状況・更新履歴・
// 承認依頼＆アップロードのステータス表示を含む。ページ側は Main で包むのみ）
const ArticleEditForm = ({
  siteId,
  articleProposalId,
  detail,
  enableArticleUpload,
  onSaved
}: Props) => {
  const { showModal: showArticleDiagnosis } = useContext(ArticleDiagnosisModalContext)
  const { showModal: showArticleRewrite } = useContext(ArticleRewriteModalContext)
  const {
    feature_page: featurePage,
    latest_article: latest,
    history,
    latest_upload: upload
  } = detail

  const {
    register,
    handleSubmit,
    control,
    setValue,
    getValues,
    reset,
    formState: { errors, isDirty, isSubmitting }
  } = useForm<ArticleEditSchemaType>({
    resolver: zodResolver(articleEditSchema),
    defaultValues: convertDetailToSchema(detail)
  })

  useAppHeaderHiding(isDirty)
  const [messages, setMessages] = useMessageStates()
  useFormErrorMessages(errors, setMessages, articleEditNameLabelMapping)
  const showSaved = useShowSavedToast({ editing: true })

  const navigate = useNavigate()
  const { addMessage } = useContext(ToastMessageContext)
  const [uploading, setUploading] = useState(false)
  const { trigger: uploadImage } = useUploadArticleImageMutation()
  const { trigger: updateArticle, isMutating: saving } = useUpdateArticleMutation({
    onSuccess: () => {
      showSaved('記事')
      onSaved()
      navigate(articleDetailPath(siteId, articleProposalId))
    }
  })

  const title = useWatch({ control, name: 'title' })
  const productListPart = findPart(detail.parts, 'product_list')
  const headerBannerImagePath = useWatch({ control, name: 'headerBannerImagePath' })
  const partPageTitle = useWatch({ control, name: 'partPageTitle' })
  const metaDescription = useWatch({ control, name: 'metaDescription' })
  const slug = useWatch({ control, name: 'slug' })
  const slugKind = useWatch({ control, name: 'slugKind' })
  const slugOriginal = useWatch({ control, name: 'slugOriginal' })
  // .liquid の拡張子（編集前のスラッグのもの。入力欄の suffix に出し、保存時に付ける）
  const slugExt = slugOriginal.slice(-ARTICLE_SLUG_EXT_STEM_EDITABLE.length)
  // 保存されるスラッグ（SERPプレビューに出す）
  const fullSlug =
    slugKind === ArticleSlugKind.FIXED
      ? slugOriginal
      : slugKind === ArticleSlugKind.STEM_EDITABLE
        ? slug + slugExt
        : slug

  const onSubmit: SubmitHandler<ArticleEditSchemaType> = (data) =>
    updateArticle(
      convertSchemaToUpdateVars(siteId, detail.latest_article.id, data, productListPart)
    )

  const handleApplyRewrite = (fields: AppliedArticleRewriteFields) => {
    setValue('title', fields.title, { shouldDirty: true })
    setValue('metaDescription', fields.metaDescription, { shouldDirty: true })
    setValue('contentAreaTop', fields.contentAreaTop, { shouldDirty: true })
    setValue('contentAreaBottom', fields.contentAreaBottom, { shouldDirty: true })
    // AIリライトを使用したことを記録
    setValue('aiUsed', true, { shouldDirty: true })
  }

  // 未保存のトーン・ペルソナもリライトへ反映する
  const openArticleRewrite = () =>
    showArticleRewrite({
      siteId,
      detail,
      tone: getValues('tone'),
      persona: getValues('persona'),
      onApply: handleApplyRewrite
    })

  const [searchParams, setSearchParams] = useSearchParams()
  useEffect(() => {
    if (searchParams.get('openAiRewrite')) {
      openArticleRewrite()
      setSearchParams(
        (prev) => {
          prev.delete('openAiRewrite')
          return prev
        },
        { replace: true }
      )
    }
    // マウント時に一度だけクエリパラメーターを確認する
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleUploadImage = async (files: FileList | null) => {
    const file = files?.[0]
    if (!file) return
    setUploading(true)
    try {
      const res = await uploadImage({ site_id: siteId, file })
      setValue('headerBannerImagePath', res.image_path, { shouldDirty: true })
    } catch {
      addMessage({ text: '画像のアップロードに失敗しました', type: MessageType.CRITICAL })
    } finally {
      setUploading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} onKeyDown={preventKeyDown}>
      <PageTitle
        title={latest.title || '（無題の記事）'}
        badgeNode={<AppTitleBadge type={AppTitleBadgeType.MEDIA} />}
        behindNode={
          <Badge badgeType={articleStatusBadgeType(latest.status)} fixed>
            {articleStatusLabel(latest.status)}
          </Badge>
        }
        rightNode={
          <>
            <AiButton size={'large'} onClick={openArticleRewrite}>
              AIリライトする
            </AiButton>
            <AiButton
              size={'large'}
              onClick={() => showArticleDiagnosis({ siteId, articleProposalId })}
            >
              AI診断する
            </AiButton>
          </>
        }
      />
      {isDirty && (
        <TopBar
          status={TopBarStatus.CHANGE}
          captionText={'未反映の変更があります'}
          onCancel={() => reset(convertDetailToSchema(detail))}
          loading={isSubmitting}
          withSide
        />
      )}
      <BackButton link={articleDetailPath(siteId, articleProposalId)} />
      <FormMessageList messageStates={messages} setMessageStates={setMessages} />
      <ContentSideFrame>
        <Content type={ContentType.TWO_COLUMNS_LEFT}>
          <Area bottomMargin={16}>
            <Heading>特集記事概要</Heading>
            <AreaContent>
              <FormRow>
                <FormLabel>記事ID</FormLabel>
                <Text size={12}>{featurePage.feature_page_id ?? '-'}</Text>
              </FormRow>
              <FormRow>
                <FormLabel>カテゴリ</FormLabel>
                <Text size={12}>{featurePage.category_id || '-'}</Text>
              </FormRow>
              <FormRow>
                <FormLabel>管理ページ名</FormLabel>
                <Text size={12}>{featurePage.management_title || '-'}</Text>
              </FormRow>
              <FormRow>
                <FormLabel>記事URL（スラッグ）</FormLabel>
                {/* W2 の特集ページのファイル名：.aspx は変更できず、.liquid は拡張子を除いたファイル名だけ変更できる */}
                <FormTextField
                  register={register('slug')}
                  error={!!errors.slug}
                  size={TextBoxSize.FULL}
                  disabled={slugKind === ArticleSlugKind.FIXED}
                  suffix={
                    slugKind === ArticleSlugKind.STEM_EDITABLE
                      ? slugExt
                      : slugKind === ArticleSlugKind.FIXED
                        ? undefined
                        : slug.length + '/' + ARTICLE_SLUG_MAX_LENGTH
                  }
                />
              </FormRow>
              <FormError message={errors.slug?.message} />
              <FormRow>
                <FormLabel>HTMLページタイトル</FormLabel>
                <TooltipWrapper
                  text={'SEO観点では30~40文字が適切です。'}
                  className='flex-1'
                  off={title.length < ARTICLE_TITLE_MAX_LENGTH - 10}
                >
                  <FormTextField
                    register={register('title')}
                    error={!!errors.title}
                    size={TextBoxSize.FULL}
                    suffix={title.length + '/' + ARTICLE_TITLE_MAX_LENGTH}
                  />
                </TooltipWrapper>
              </FormRow>
              <FormRow>
                <FormLabel>SEOディスクリプション</FormLabel>
                <FormTextArea
                  name='metaDescription'
                  register={register('metaDescription')}
                  control={control}
                  error={!!errors.metaDescription}
                  counter
                  maxLength={160}
                />
              </FormRow>
              <FormRow>
                <FormLabel>SERPプレビュー</FormLabel>
                <SerpPreview slug={fullSlug} pageTitle={title} metaDescription={metaDescription} />
              </FormRow>
            </AreaContent>
          </Area>
          <Area bottomMargin={16}>
            <Heading>記事情報</Heading>
            <AreaContent>
              <FormRow>
                <FormLabel>キーワード</FormLabel>
                <FormTextArea
                  name='keyword'
                  register={register('keyword')}
                  control={control}
                  error={!!errors.keyword}
                  counter
                  maxLength={2000}
                  minRows={2}
                />
              </FormRow>
              <FormError message={errors.keyword?.message} />
              <FormRow>
                <FormLabel>見出し構成案</FormLabel>
                <FormTextArea
                  name='headingOutline'
                  register={register('headingOutline')}
                  control={control}
                  error={!!errors.headingOutline}
                  minRows={4}
                />
              </FormRow>
              <FormRow>
                <FormLabel>トーン</FormLabel>
                <FormRadioButton
                  control={control}
                  name='tone'
                  choices={TONE_RADIO_CHOICES}
                  input
                  direction='horizon'
                />
              </FormRow>
              <FormRow>
                <FormLabel subText='記事の想定される読み手。AI記事生成・AIリライトのプロンプトへ反映されます'>
                  ペルソナ
                </FormLabel>
                <FormTextArea
                  name='persona'
                  register={register('persona')}
                  control={control}
                  error={!!errors.persona}
                  maxLength={ARTICLE_PERSONA_MAX_LENGTH}
                  placeholder={PERSONA_PLACEHOLDER}
                  minRows={3}
                />
              </FormRow>
              <FormRow>
                <FormLabel>目標文字数</FormLabel>
                <FormSelector
                  register={register('targetCharCount', registerNumberOption)}
                  choices={TARGET_CHAR_COUNT_CHOICES}
                  size={SelectorSize.MEDIUM}
                />
              </FormRow>
            </AreaContent>
          </Area>
          <Area>
            <Heading>コンテンツブロック</Heading>
            <AreaContent>
              <div className='flex flex-col gap-4'>
                <FormRow>
                  <FormLabel fixWidth={null}>ページタイトル</FormLabel>
                  <TooltipWrapper
                    text={'SEO観点では30~40文字が適切です。'}
                    className='flex-1'
                    off={partPageTitle.length < ARTICLE_TITLE_MAX_LENGTH - 10}
                  >
                    <FormTextField
                      register={register('partPageTitle')}
                      error={!!errors.partPageTitle}
                      size={TextBoxSize.FULL}
                      suffix={partPageTitle.length + '/' + ARTICLE_TITLE_MAX_LENGTH}
                    />
                    <FormError message={errors.partPageTitle?.message} />
                  </TooltipWrapper>
                </FormRow>
                <div className='flex flex-col gap-1'>
                  <FormLabel fixWidth={null}>ヘッダーバナー画像</FormLabel>
                  <UploadDropZone
                    buttonText='ファイルを追加する'
                    fileName={
                      headerBannerImagePath ? headerBannerImagePath.split('/').pop() : undefined
                    }
                    filePath={headerBannerImagePath || undefined}
                    imageSize={UploadDropZoneImageSize.BANNER}
                    onlyImageFile
                    disabled={uploading}
                    onChange={(e) => {
                      handleUploadImage(e.currentTarget.files).catch(() => undefined)
                    }}
                    onDelete={() => setValue('headerBannerImagePath', '', { shouldDirty: true })}
                  />
                </div>
                <div className='flex flex-col gap-1'>
                  <FormLabel fixWidth={null}>コンテンツエリア上部</FormLabel>
                  <FormTextArea
                    name='contentAreaTop'
                    register={register('contentAreaTop')}
                    control={control}
                    minRows={8}
                    maxLength={StringLength.HTML_MAX}
                  />
                </div>
                <div className='flex flex-col gap-1'>
                  <FormLabel fixWidth={null}>商品一覧</FormLabel>
                  <InsideArea color={BgColor.SECONDARY}>
                    <Text size={12} color={TextColor.SECONDARY}>
                      {articleProductListSummary(productListPart?.content_html)}
                    </Text>
                  </InsideArea>
                </div>
                <div className='flex flex-col gap-1'>
                  <FormLabel fixWidth={null}>コンテンツエリア下部</FormLabel>
                  <FormTextArea
                    name='contentAreaBottom'
                    register={register('contentAreaBottom')}
                    control={control}
                    minRows={8}
                  />
                </div>
              </div>
            </AreaContent>
          </Area>
        </Content>

        <Side>
          <Area bottomMargin={16}>
            <Heading>利用状況</Heading>
            <AreaContent>
              <FormRow>
                <FormLabel fixWidth={80}>ステータス</FormLabel>
                <FormDisclosureSelector
                  control={control}
                  name='status'
                  choices={articleStatusChoices(enableArticleUpload)}
                  disabled={latest.status === ArticleStatus.PENDING_APPROVAL}
                />
              </FormRow>
              <FormRow align='center'>
                <FormLabel fixWidth={80}>最終更新者</FormLabel>
                <Text size={12}>{latest.site_operator_name || '-'}</Text>
              </FormRow>
              <FormRow align='center'>
                <FormLabel fixWidth={80}>最終更新日</FormLabel>
                <Text size={12}>{latest.updated_at ? dateTimeStr(latest.updated_at) : '-'}</Text>
              </FormRow>
            </AreaContent>
          </Area>
          <ArticleUpdateHistory
            siteId={siteId}
            articleProposalId={articleProposalId}
            history={history}
          />
        </Side>
      </ContentSideFrame>
      <PageBottom>
        <Button color={ButtonColor.PRIMARY_BRAND} size='large' loading={saving} type={'submit'}>
          保存する
        </Button>
      </PageBottom>
    </form>
  )
}

export default ArticleEditForm
